import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import { isJrThruTrain, isTokyuThruTrain, isTokyuTrain } from '../../utils/TrainNumber'
import MasterData from '../../data/MasterData'
import { tidTrainKindClass } from '../../utils/ClassHelper'

const SingleTrainModal = (props) => {
  const {
    trains,
    showingTrain,
    themeName,
    fetchTrains,
    fetchJrTrains,
    fetchTokyuTrains,
    fetchSingleTrain,
    fetchSingleJrTrain,
    closeTrainModal,
  } = props

  const intl = useIntl()
  const locale = intl.locale
  const navigate = useNavigate()
  const { trainNumber } = useParams()
  useEffect(() => {
    const fetchData = async () => {
      if (showingTrain === null) {
        const [fetchedSotetsuTrains, fetchedJrTrains, fetchedTokyuTrains] = await Promise.all([
          fetchTrains(),
          isJrThruTrain(trainNumber) ? fetchJrTrains() : Promise.resolve([]),
          isTokyuThruTrain(trainNumber)
            ? Promise.all([
                fetchTokyuTrains('toyoko'),
                fetchTokyuTrains('meguro'),
                fetchTokyuTrains('shinyokohama'),
              ]).then(results => results.flat())
            : Promise.resolve([]),
        ])

        const allTrains = [
          ...(fetchedSotetsuTrains || []),
          ...(fetchedJrTrains || []),
          ...(fetchedTokyuTrains || []),
        ]
        const train = allTrains.find(t => t.train_number === trainNumber)
        if (train) {
          await fetchSingleTrain(train)
        }
        if (isJrThruTrain(trainNumber)) {
          await fetchSingleJrTrain(train)
        }
      }
    }

    fetchData()

    return () => {
      closeTrainModal()
    }
  }, [])

  const currentPosition = (tidTrain) => {
    if (tidTrain.station_id) {
      const stoppingStation = MasterData.station(tidTrain.station_id)
      return stoppingStation[`name_${locale}`]
    } else {
      var fromStation, nextStation, trainLineId
      trainLineId = (tidTrain.source === 'tokyu' ? tidTrain.train_line_id : null)

      if (tidTrain.direction === 'up') {
        nextStation = MasterData.station(tidTrain.next_station_id)
        const fromStationId = MasterData.downNextStationId(tidTrain.line_id, tidTrain.next_station_id)
        fromStation = MasterData.station(fromStationId)
      } else {
        nextStation = MasterData.station(tidTrain.next_station_id)
        const fromStationId = MasterData.upNextStationId(tidTrain.line_id, tidTrain.next_station_id, trainLineId)
        fromStation = MasterData.station(fromStationId)
      }
      return `${fromStation[`name_${locale}`]}〜${nextStation[`name_${locale}`]}`
    }
  }

  const stoppingStations = (stationId, nextStationId, stopStations) => {
    let nextStopStationIndex
    if (stationId) {
      nextStopStationIndex = stopStations.findIndex(ss => ss.station_id === stationId) + 1
    } else {
      nextStopStationIndex = stopStations.findIndex(ss => ss.station_id === nextStationId)
    }
    return stopStations.slice(nextStopStationIndex)
  }

  const handleCloseClick = (e) => {
    navigate(-1)
  }

  const estimatedArrivalTimeElement = (station, hasIncidents = false) => {
    if (hasIncidents) {
      return <p className="c-Modal-timetableItemTimeNow is_na">-</p>
    } else if (0 < showingTrain.tidTrain.delay) {
      return <p className="c-Modal-timetableItemTimeNow is_delay">{station[`estimated_arrival_time_${locale}`]}</p>
    } else {
      return <p className="c-Modal-timetableItemTimeNow">{station.supposed_arrival_time}</p>
    }
  }

  // 上部の青いヘッダー
  const renderHeader = () => {
    return (
      <div className={`c-Modal-header is_${themeName}`}>
        <FormattedMessage id='App.ModalDialog.headerTitle'>
          {(txt) => <p className="c-Modal-headerTitle">{txt}</p>}
        </FormattedMessage>
        <div className="c-Modal-headerClose" onClick={handleCloseClick}></div>
      </div>
    )
  }

  const renderDelay = () => {
    const { delay } = showingTrain.tidTrain
    if (100 <= delay) {
      return (
        <p className="c-Modal-infoStatus is_delay">
          <FormattedMessage id='App.ModalDialog.significantlyDelayed' />
        </p>
      )
    } else if (0 < delay) {
      return (
        <FormattedMessage id='App.ModalDialog.minLate'>
          {(txt) => <p className="c-Modal-infoStatus is_delay">{delay}{txt}</p>}
        </FormattedMessage>
      )
    } else {
      // 遅延なし
      return (
        <p className="c-Modal-infoStatus">
          <FormattedMessage id='App.ModalDialog.onTime' />
        </p>
      )
    }
  }

  // 種別、両数、行き先、現在値などの表示
  const renderTrainAttributes = () => {
    const { apiTrain, tidTrain } = showingTrain
    const currentPositionStr = currentPosition(tidTrain)

    if (!apiTrain) {
      return (
        <div className="c-Modal-info">
          <div className="c-Modal-infoRow">
            <ul className="c-Modal-infotag">
              <li className={`c-Modal-infoItem ${tidTrainKindClass(tidTrain)}`}>{MasterData.trainKindById(tidTrain.train_kind_id)[`name_${locale}`]}</li>
            </ul>
          </div>
          <div className="c-Modal-infoRow">
            <FormattedMessage id='App.ModalDialog.currently'>
              {(txt) => <p className="c-Modal-infoPlace">{txt}：{currentPositionStr}</p>}
            </FormattedMessage>
            <div className="c-Modal-infoAlert">
            </div>
            <div className="c-Modal-infoNote">
              <FormattedMessage id='App.ModalDialog.kindOrDestinationsMayChange' />
            </div>
          </div>
        </div>
      )
    }
    return (
      <div className="c-Modal-info">
        <div className="c-Modal-infoRow">
          <ul className="c-Modal-infotag">
            <li className={`c-Modal-infoItem ${tidTrainKindClass(tidTrain)}`}>{MasterData.trainKindById(tidTrain.train_kind_id)[`name_${locale}`]}</li>
            {!showingTrain.hasIncidents && apiTrain.train_length && (
              <FormattedMessage id='App.ModalDialog.cars'>
              {(txt) => <li className="c-Modal-infoItem is_count">{apiTrain.train_length}{txt}</li>}
              </FormattedMessage>
            )}
          </ul>
          {!showingTrain.hasIncidents && (
            <div className="c-Modal-infoName">{apiTrain[`destination_station_name_${locale}`]}</div>
          )}
          </div>
        <div className="c-Modal-infoRow">
          <FormattedMessage id='App.ModalDialog.currently'>
            {(txt) => <p className="c-Modal-infoPlace">{txt}：{currentPositionStr}</p>}
          </FormattedMessage>
          {showingTrain.hasIncidents && (
            <div className="c-Modal-infoAlert">
              <FormattedMessage id='App.ModalDialog.arrivalTimeNotAvailable' />
            </div>
          )}
          {!showingTrain.hasIncidents && renderDelay()}
          <div className="c-Modal-infoNote">
            <FormattedMessage id='App.ModalDialog.kindOrDestinationsMayChange' />
          </div>
        </div>
      </div>
    )
  }

  // 運行異常時の文言表示
  // TODO: スタイルを適用
  const renderDisableMessage = () => {
    return (
      <div className="c-Modal-timetable">
        <div className="c-Modal-timetableBody">
          <p style={{ textAlign: 'center', fontSize: '1.25rem', marginTop: '2rem', whiteSpace: 'pre' }}><strong><FormattedMessage id='App.ModalDialog.sorryArrivalTimeNotAvailable' /></strong></p>
        </div>
      </div>
    )
  }

  const renderStopStationEmptyMessage = () => {
    return (
      <div className="c-Modal-timetable">
        <div className="c-Modal-timetableBody">
          <p style={{ textAlign: 'center', fontSize: '1.25rem', marginTop: '2rem', whiteSpace: 'pre' }}><strong><FormattedMessage id='App.ModalDialog.arrivalTimeNotAvailable' /></strong></p>
        </div>
      </div>
    )
  }

  // 停車駅領域のヘッダー部分
  const renderStoppingStationsHead = () => {
    return (
      <div className="c-Modal-timetableHeader">
        <FormattedMessage id='App.ModalDialog.trainStops'>
          {(txt) => <p className="c-Modal-timetableHeaderStation">{txt}</p>}
        </FormattedMessage>
        <FormattedMessage id='App.ModalDialog.arrivesScheduled'>
          {(txt) => <p className="c-Modal-timetableHeaderPlan">{txt}</p>}
        </FormattedMessage>
        <FormattedMessage id='App.ModalDialog.estimated'>
          {(txt) => <p className="c-Modal-timetableHeaderNow">{txt}</p>}
        </FormattedMessage>
      </div>
    )
  }

  const renderArrivedNotice = () => {
    return (
      <div className="c-Modal-timetable">
        <div className="c-Modal-timetableBody">
          <div className="c-Modal-timetableArrivedNotice">
            到着済み
          </div>
        </div>
      </div>
    )
  }

  // 停車駅表示
  const renderStopStations = () => {
    const { tidTrain, apiTrain, stopStations, jrStopStations } = showingTrain

    const isJr = MasterData.isSectionOnJrLine(tidTrain.station_id, tidTrain.next_station_id, tidTrain.direction) && isJrThruTrain(trainNumber)

    const showingStopStations = isJr ? jrStopStations : stopStations
    if (!showingStopStations) {
      return renderStopStationEmptyMessage()
    }

    const stoppingStationsData = stoppingStations(tidTrain.station_id, tidTrain.next_station_id, showingStopStations)

    if (stoppingStationsData.length === 0) {
      return renderArrivedNotice()
    } else {
      return (
        <div className="c-Modal-timetable">
          {renderStoppingStationsHead()}

          <div className="c-Modal-timetableBody">
            <ul className="c-Modal-timetableList">
              {stoppingStationsData.map((station) => renderStopStationRow(station))}
              {renderFurtherArrivalTimeNotAvailableRow(tidTrain, apiTrain)}
            </ul>
          </div>
        </div>
      )
    }
  }

  // 停車駅表示の駅ごとの行表示
  const renderStopStationRow = (station) => {

    return (
      <li key={`timetableItem${station.station_id}`} className="c-Modal-timetableItem">
        <p className="c-Modal-timetableItemStation">{station[`name_${locale}`]}</p>
        {station.will_stop ? (
          <div className="c-Modal-timetableItemTime">
            <p className="c-Modal-timetableItemTimePlan">
              {showingTrain.hasIncidents ? '-' : station.supposed_arrival_time}
            </p>
            {estimatedArrivalTimeElement(station, showingTrain.hasIncidents)}
          </div>
        ) : (
          <div className="c-Modal-timetableItemTime">
            <FormattedMessage id='App.ModalDialog.doesNotStop'>
              {(txt) => <p className="c-Modal-timetableItemPassage">{txt}</p>}
            </FormattedMessage>
          </div>
        )}
        <div className={`c-Modal-timetableItemLine ${station.will_stop ? '' : 'is_passage'}`}></div>
      </li>
    )
  }

  const renderFurtherArrivalTimeNotAvailableRow = (tidTrain, apiTrain) => {
    const isJr = MasterData.isSectionOnJrLine(tidTrain.station_id, tidTrain.next_station_id, tidTrain.direction) && isJrThruTrain(trainNumber)
    const isJrDestination = MasterData.isOnJrStation(tidTrain.destination_station_id)
    const isDestinationBeyondShinjuku = MasterData.isOnJrStationBeyondShinjuku(apiTrain.destination_station_id)

    const isOnToyokoTowardDestination = MasterData.isOnToyokoToward(tidTrain.destination_station_id)
    const isOnMeguroTowardDestination = MasterData.isOnMeguroToward(tidTrain.destination_station_id)
    const isOnTokyuLineDestination = MasterData.isOnTokyuLine(tidTrain.destination_station_id)
    const isOnToyokoLineMotomachiToward = MasterData.isOnToyokoLineMotomachiToward(tidTrain.destination_station_id)

    if ((isJr && tidTrain.direction === 'down') || (!isJr && isJrDestination && tidTrain.direction === 'up') || (isJr && isDestinationBeyondShinjuku && tidTrain.direction === 'up')){
      return (
        <li key="timetableItemNotAvailable" className="c-Modal-timetableItem">
          <FormattedMessage id='App.ModalDialog.furtherArrivalTimeNotAvailable'>
              {(txt) => <div className="c-Modal-timetableItemNotAvailable">{txt}</div>}
          </FormattedMessage>
        </li>
      )
    } else if (isOnToyokoLineMotomachiToward || ((isOnToyokoTowardDestination || isOnMeguroTowardDestination) && !isOnTokyuLineDestination)) {
      // 副都心線方面、南北線方面、三田線方面かつ東急線内（渋谷・目黒〜日吉）間でない
      return (
        <li key="timetableItemNotAvailable" className="c-Modal-timetableItem">
          &nbsp;
        </li>
      )
    }
  }
  
  // 画面の下半分を描画する
  // 運行異常時かを判定して、適切なものを表示する
  const renderTimetableBlock = () => {
    const { apiTrain, tidTrain } = showingTrain

    if(!apiTrain) {
      return renderStopStationEmptyMessage()
    }

    // TODO: 快速B,特急BはNavitimeの種別と異なっているので正常とみなす。
    // JR線区間に在線している場合には種別判定を行わない。
    // 東急直通列車・東急線列車（列車番号6桁）は種別判定を行わない
    if (MasterData.isSectionOnJrLine(tidTrain.station_id, tidTrain.next_station_id, tidTrain.direction) || apiTrain.kind === tidTrain.train_kind_id || tidTrain.train_kind_id === 7 || tidTrain.train_kind_id === 8 || isTokyuTrain(tidTrain.train_number)) {
      return renderStopStations()
    } else {
      // TIDの種別は運行異常により急遽変更される場合がある。その場合時刻表を出さない。
      return renderDisableMessage()
    }
  }

  if (showingTrain === null) return null

  return (
    <div className="c-Modal">
      <div className="c-Modal-dialog">
        {renderHeader()}
        {renderTrainAttributes()}
        {renderTimetableBlock()}
      </div>
    </div>
  )
}

SingleTrainModal.propTypes = {
  trains: PropTypes.array.isRequired,
  showingTrain: PropTypes.object,
  themeName: PropTypes.string.isRequired,
  fetchTrains: PropTypes.func.isRequired,
  fetchJrTrains: PropTypes.func.isRequired,
  fetchTokyuTrains: PropTypes.func.isRequired,
  fetchSingleTrain: PropTypes.func.isRequired,
  fetchSingleJrTrain: PropTypes.func.isRequired,
  closeTrainModal: PropTypes.func.isRequired,
}

export default SingleTrainModal
